/**
 * Wires the pace math to the four things it depends on: the catalog's
 * deadlines, the road as currently filtered, what you've watched, and the
 * clock.
 *
 * The scope is the **filtered** road, not the whole catalog — see the note in
 * legs.ts. It's also costed exactly the way the control panel's tally is
 * (`filters.useAlternatives`), so the bar and the panel can never quote two
 * different totals for the same road.
 *
 * `now` moves every second, so this recomputes every second: a few hundred
 * additions over ~81 entries, and only the components that subscribe to the
 * clock re-render.
 */

import { useMemo } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { useFilteredRoad } from "@/lib/filters/context.tsx";
import { useNow } from "@/state/countdown.ts";
import { useProgressStore } from "@/state/progress.ts";
import { useSettingsStore } from "@/state/settings.ts";
import { type CountdownState, computeCountdown } from "./legs.ts";

export function useCountdown(): CountdownState {
  const catalog = useCatalog();
  const { entries, filters } = useFilteredRoad();
  const watched = useProgressStore((state) => state.watched);
  const encoreMode = useSettingsStore((state) => state.encoreMode);
  const hoursPerWeek = useSettingsStore((state) => state.hoursPerWeek);
  const now = useNow();

  // The first milestone that actually splits the road. A catalog without one
  // simply has no Encore mode (computeCountdown forces it off).
  const milestone = useMemo(
    () => catalog.milestones.find((m) => m.splitAfterEntryId !== null) ?? null,
    [catalog],
  );

  return useMemo(
    () =>
      computeCountdown({
        now,
        entries,
        watched,
        target: catalog.target,
        milestone,
        encoreMode,
        hoursPerWeek,
        useAlternatives: filters.useAlternatives,
      }),
    [
      now,
      entries,
      watched,
      catalog.target,
      milestone,
      encoreMode,
      hoursPerWeek,
      filters.useAlternatives,
    ],
  );
}
